import React, { useEffect, useMemo, useState } from 'react';
import {
  MapContainer,
  TileLayer,
  CircleMarker,
  Popup,
  GeoJSON,
  LayersControl,
  LayerGroup,
  useMap,
} from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import { useDeal } from '../../deals/DealContext';
import { dealDataUrl } from '../../deals/registry';
import { apiKey } from '../../utils/wellIdentity';
import { leafletTileLayerProps, resolveBasemap } from '../../utils/mapBasemap';
import {
  findFirstSelectedWell,
  isMultiSelectModifier,
  toggleWellSelection,
} from '../../utils/wellMapSelection';

const STATUS_COLOR = {
  PDP: '#10B981',
  PDSI: '#E8A317',
  'P&A': '#94A3B8',
};

const LEASE_FILL = {
  'Certified Unit': '#1B355C',
  HBP: '#0F766E',
  'Non HBP': '#B45309',
  Expired: '#DC2626',
};

function isValidLatLngBounds(bounds) {
  if (!Array.isArray(bounds) || bounds.length !== 2) return false;
  const [[south, west], [north, east]] = bounds;
  return [south, west, north, east].every(Number.isFinite);
}

function FitBounds({ bounds }) {
  const map = useMap();
  useEffect(() => {
    if (!isValidLatLngBounds(bounds)) return;
    map.fitBounds(bounds, { padding: [28, 28], maxZoom: 12 });
  }, [map, bounds]);
  return null;
}

function wellRadius(pv10M) {
  if (pv10M == null || Number.isNaN(pv10M)) return 6;
  // pv10M is in $000s in ARIES extract (e.g. 8610 = $8.61MM)
  const mm = pv10M > 100 ? pv10M / 1000 : pv10M;
  return Math.max(5, Math.min(18, 4 + Math.sqrt(Math.max(mm, 0)) * 3.2));
}

function leaseStyle(feature) {
  const status = feature?.properties?.TRACT_STAT || feature?.properties?.tract_st_1 || '';
  let fill = '#64748B';
  for (const [key, color] of Object.entries(LEASE_FILL)) {
    if (status.toLowerCase().includes(key.toLowerCase())) {
      fill = color;
      break;
    }
  }
  if (/certified/i.test(status)) fill = LEASE_FILL['Certified Unit'];
  return {
    color: fill,
    weight: 0.6,
    opacity: 0.85,
    fillColor: fill,
    fillOpacity: 0.18,
  };
}

function unitStyle() {
  return {
    color: '#E8A317',
    weight: 1.6,
    opacity: 0.95,
    fillColor: '#E8A317',
    fillOpacity: 0.06,
  };
}

function onEachLease(feature, layer) {
  const p = feature.properties || {};
  const acres = p.CalcAcre || p.Acres;
  layer.bindPopup(
    `<div class="map-popup"><strong>Tract ${p.TRACT_NO || '—'}</strong><br/>${p.TRACT_STAT || p.tract_st_1 || '—'}${
      acres ? `<br/>${Number(acres).toFixed(1)} ac` : ''
    }</div>`
  );
}

function onEachUnit(feature, layer) {
  const p = feature.properties || {};
  const acres = p.CalcAcre || p.Acres;
  layer.bindPopup(
    `<div class="map-popup"><strong>${p.Name || 'Drilling unit'}</strong><br/>${p.Lease_Date || '—'}${
      acres ? `<br/>${Number(acres).toFixed(1)} ac` : ''
    }</div>`
  );
}

function wellKey(w) {
  return apiKey(w?.api) || w?.api || '';
}

export default function WellAcreageMap({
  data,
  statusFilter = 'ALL',
  showPvSize = true,
  onWellSelect,
  selectedWellKeys,
  onSelectionChange,
  defaultSelectedWellKeys = [],
  /** Full well directory for Open when markers are filtered out of `data.wells`. */
  resolveWells,
}) {
  const { deal } = useDeal();
  const [unitsGeo, setUnitsGeo] = useState(null);
  const [leasesGeo, setLeasesGeo] = useState(null);
  const [internalSelected, setInternalSelected] = useState(defaultSelectedWellKeys);
  const basemap = useMemo(() => resolveBasemap(), []);

  const isControlled = selectedWellKeys != null;
  const selected = isControlled ? selectedWellKeys : internalSelected;

  const setSelected = (next) => {
    if (!isControlled) setInternalSelected(next);
    onSelectionChange?.(next);
  };

  useEffect(() => {
    const unitsPath = data.layers?.drillingUnits || null;
    const leasesPath = data.layers?.leaseStatus || null;
    // No Eagle geojson fallback — oil / economics-only deals omit layers.
    if (!unitsPath && !leasesPath) {
      setUnitsGeo(null);
      setLeasesGeo(null);
      return undefined;
    }
    let cancelled = false;
    const load = async (path) => {
      if (!path) return null;
      const url = dealDataUrl(deal, path, import.meta.env.BASE_URL);
      const res = await fetch(url, { credentials: 'include' });
      if (!res.ok) return null;
      return res.json();
    };
    Promise.all([load(unitsPath), load(leasesPath)])
      .then(([units, leases]) => {
        if (cancelled) return;
        setUnitsGeo(units);
        setLeasesGeo(leases);
      })
      .catch((err) => console.error('Failed to load map layers', err));
    return () => {
      cancelled = true;
    };
  }, [data.layers, deal]);

  const wells = useMemo(() => {
    const list = data.wells || [];
    if (statusFilter === 'ALL') return list;
    return list.filter((w) => w.status === statusFilter);
  }, [data.wells, statusFilter]);

  const selectedSet = useMemo(() => new Set(selected || []), [selected]);

  const handleMarkerClick = (w, leafletEvent) => {
    const key = wellKey(w);
    if (!key) return;
    const multi = isMultiSelectModifier(leafletEvent?.originalEvent);
    const next = toggleWellSelection(selected || [], key, { multi });
    setSelected(next);
    if (multi && leafletEvent?.target?.closePopup) {
      leafletEvent.target.closePopup();
    }
  };

  const clearSelection = () => setSelected([]);

  const openFirstSelected = () => {
    if (!onWellSelect || !selected?.length) return;
    // Visible markers first, then unfiltered data.wells (status filter), then
    // optional full directory (Wells finder may narrow data.wells itself).
    const first = findFirstSelectedWell(
      selected,
      [wells, data.wells, resolveWells],
      wellKey
    );
    if (first) onWellSelect(first);
  };

  const centerLat = Number(data.center?.lat);
  const centerLng = Number(data.center?.lng);
  const center = [
    Number.isFinite(centerLat) ? centerLat : 31.17,
    Number.isFinite(centerLng) ? centerLng : -96.53,
  ];
  const bounds = isValidLatLngBounds(data.bounds) ? data.bounds : null;
  const selectedCount = selected?.length || 0;
  const tileProps = useMemo(() => leafletTileLayerProps(basemap), [basemap]);

  return (
    <div className="well-acreage-map">
      {selectedCount > 0 && (
        <div className="map-selection-toolbar" role="status" aria-live="polite">
          <span className="map-selection-count">
            {selectedCount} well{selectedCount === 1 ? '' : 's'} selected
          </span>
          <button type="button" className="btn-secondary map-selection-btn" onClick={clearSelection}>
            Clear
          </button>
          {onWellSelect && (
            <button
              type="button"
              className="btn-secondary map-selection-btn"
              onClick={openFirstSelected}
            >
              {selectedCount === 1 ? 'Open detail' : 'Open first'}
            </button>
          )}
        </div>
      )}
      <MapContainer
        center={center}
        zoom={11}
        scrollWheelZoom
        className="well-acreage-map-canvas"
      >
        <TileLayer {...tileProps} />
        {bounds && <FitBounds bounds={bounds} />}

        <LayersControl position="topright">
          <LayersControl.Overlay checked name="Lease tracts (2021)">
            {leasesGeo && (
              <GeoJSON
                key="leases"
                data={leasesGeo}
                style={leaseStyle}
                onEachFeature={onEachLease}
              />
            )}
          </LayersControl.Overlay>
          <LayersControl.Overlay checked name="Drilling units">
            {unitsGeo && (
              <GeoJSON
                key="units"
                data={unitsGeo}
                style={unitStyle}
                onEachFeature={onEachUnit}
              />
            )}
          </LayersControl.Overlay>
          <LayersControl.Overlay checked name="Wells">
            <LayerGroup>
              {wells.map((w) => {
                const lat = Number(w.lat);
                const lng = Number(w.lng);
                if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
                const key = wellKey(w);
                const isBoswell = /BOSWELL/i.test(w.lease);
                const isSelected = key && selectedSet.has(key);
                const color = STATUS_COLOR[w.status] || '#64748B';
                const r = showPvSize ? wellRadius(w.pv10M) : 7;
                const radius = isSelected
                  ? Math.max(isBoswell ? Math.max(r, 14) : r, 8) + 1.5
                  : isBoswell
                    ? Math.max(r, 14)
                    : r;
                return (
                  <CircleMarker
                    key={w.api}
                    center={[lat, lng]}
                    radius={radius}
                    pathOptions={{
                      color: isSelected ? '#0C1F2E' : isBoswell ? '#0C1F2E' : '#ffffff',
                      weight: isSelected ? 3.5 : isBoswell ? 2.5 : 1.2,
                      fillColor: color,
                      fillOpacity: isSelected ? 1 : 0.92,
                    }}
                    eventHandlers={{
                      click: (e) => handleMarkerClick(w, e),
                    }}
                  >
                    <Popup>
                      <div className="map-popup">
                        <strong>{w.lease}</strong>
                        <div>
                          {w.status} · {w.wellType}
                        </div>
                        <div>API {w.api}</div>
                        <div>
                          {w.county} · WI {w.wi}% · NRI {w.nri}%
                        </div>
                        {w.pv10M != null && (
                          <div>
                            PV10 ${(w.pv10M > 100 ? w.pv10M / 1000 : w.pv10M).toFixed(2)}MM
                            {w.netGasResMmcf != null &&
                              ` · ${Number(w.netGasResMmcf).toFixed(0)} MMcf net`}
                          </div>
                        )}
                        {w.rrcSymbol && <div className="map-popup-muted">{w.rrcSymbol}</div>}
                        {onWellSelect && (
                          <button
                            type="button"
                            className="map-popup-link"
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              onWellSelect(w);
                            }}
                          >
                            Open well detail →
                          </button>
                        )}
                      </div>
                    </Popup>
                  </CircleMarker>
                );
              })}
            </LayerGroup>
          </LayersControl.Overlay>
        </LayersControl>
      </MapContainer>
    </div>
  );
}
